
import React, { useEffect } from "react";
import {
  Mail, Phone, MapPin, ArrowUpRight,
  GraduationCap, Landmark, ShieldCheck,
  Users, Award, Target, Eye, Lightbulb,
  Sparkles, ChevronRight, Building2,
  Scale, BookOpen, Crown, UserCheck,
  BriefcaseBusiness
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" }
  }
};

const members = [
  {
    id: 1,
    image: "/assets/img/bot-chairman.jpeg",
    name: "Brig. Gen. (Retd.) Shah Md. Sultan Uddin Iqbal, Bir Protik, ndu, psc, MBA, PhD",
    designation: "Chairman, Bandarban University Trust"
  },
  {
    id: 2,
    image: "/assets/img/nurul-absar.jpeg",
    name: "Dr. Mohammad Nurul Absar",
    designation: "Member Secretary, Bandarban University Trust"
  },
  {
    id: 3,
    image: "/assets/img/nurul-islam-bu.jpeg",
    name: "Professor Dr. M. Nurul Islam",
    designation: "Treasurer, Bandarban University"
  },
  {
    id: 4,
    image: "/assets/img/kazi-ahmed.jpeg",
    name: "Prof. Dr. Kazi Ahmed Nabi",
    designation: "Dean, School of Business Administration, Bandarban University"
  },
  {
    id: 5,
    image: "/assets/img/sarwar-jahan.jpeg",
    name: "Professor Sarwar Jahan",
    designation: "Member, Bandarban University Trust"
  },
  {
    id: 6,
    image: "/assets/img/nasirullah.jpeg",
    name: "Prof. Dr. Amir Mohammad Nasrullah",
    designation: "Member, Bandarban University Trust"
  },
  {
    id: 7,
    image: "/assets/images/teacher.jpg",
    name: "Divisional Commissioner, Chattagram",
    designation: "Government Nominated Member"
  },
  {
    id: 8,
    image: "/assets/img/thowhid.jpeg",
    name: "Prof. Dr. Mohamad Towhidul Islam",
    designation: "Professor & Chairman, Department of Law, University of Dhaka (UGC Nominated)"
  }
];

const principles = [
  {
    icon: Eye,
    title: "Our Vision",
    color: "bg-blue-600",
    description: "To become a leading institution of higher learning in Asia."
  },
  {
    icon: Target,
    title: "Our Mission",
    color: "bg-emerald-600",
    description: "To nurture creativity, research, and leadership through quality education."
  },
  {
    icon: Lightbulb,
    title: "Our Goals",
    color: "bg-amber-500",
    description: "Expanding research opportunities and strengthening global partnerships."
  }
];

function ProfileCard({ person, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 35 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay: (index % 4) * 0.08, duration: 0.65 }}
      whileHover={{ y: -9 }}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-md hover:shadow-2xl transition duration-300"
    >
      {/* PHOTO */}
      <div className="relative h-[270px] bg-gradient-to-br from-emerald-900 to-teal-700 overflow-hidden">

        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(white 1px, transparent 1px)",
            backgroundSize: "20px 20px"
          }}
        />

        <img
          src={person.image}
          alt={person.name}
          loading="lazy"
          className="relative w-full h-full object-cover object-top transition duration-700 group-hover:scale-105"
        />

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />

        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/90 backdrop-blur-md text-emerald-800 text-xs font-bold shadow">
            <UserCheck size={14} />
            Syndicate Member
          </span>
        </div>
      </div>

      {/* DETAILS */}
      <div className="p-6">

        <h3 className="text-lg font-extrabold leading-7 text-gray-900 group-hover:text-emerald-800 transition">
          {person.name}
        </h3>

        <div className="h-1 w-12 bg-amber-400 rounded-full mt-4 mb-4 group-hover:w-20 transition-all duration-300" />

        <p className="text-sm text-gray-500 leading-6 min-h-[72px]">
          {person.designation}
        </p>

        <div className="flex items-center gap-2 mt-5 text-emerald-700 text-sm font-bold">
          <Landmark size={16} />
          Bandarban University
        </div>

      </div>
    </motion.div>
  );
}

export default function Syndicate({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Syndicate | Bandarban University"
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="bg-[#f6f9f7] text-gray-800 overflow-hidden">

      {/* HERO SECTION */}
      <section className="relative min-h-[490px] bg-[#073b2a] text-white overflow-hidden flex items-center">

        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(#d6c18a 1px, transparent 1px)",
            backgroundSize: "28px 28px"
          }}
        />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
          className="absolute -right-36 -top-48 w-[600px] h-[600px] rounded-full border border-amber-200/20"
        />

        <div className="absolute right-0 bottom-0 w-96 h-96 bg-emerald-400/20 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-2 gap-12 items-center">

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-amber-200 text-sm mb-7">
              <Sparkles size={16} />
              Bandarban University
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              University
              <span className="block text-amber-300">
                Syndicate
              </span>
            </h1>

            <p className="text-gray-300 text-lg leading-8 mt-6 max-w-xl">
              The highest statutory governing body responsible for
              institutional governance, strategic direction and
              academic administration of Bandarban University.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">

              <a
                href="#chairman"
                className="px-7 py-3.5 rounded-xl bg-amber-300 text-[#073b2a] font-bold hover:bg-white transition flex items-center gap-2"
              >
                Meet Our Leadership
                <ArrowUpRight size={19} />
              </a>

              <a
                href="#members"
                className="px-7 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 transition"
              >
                View Members
              </a>

            </div>
          </motion.div>

          {/* GOVERNANCE GRAPHIC */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="hidden lg:flex justify-center"
          >

            <div className="relative w-[360px] h-[360px] flex items-center justify-center">

              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed border-amber-200/40"
              />

              <div className="absolute inset-8 rounded-full border border-white/10" />

              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative w-64 h-64 rounded-[40px] bg-gradient-to-br from-emerald-300 to-teal-700 shadow-2xl flex flex-col items-center justify-center"
              >
                <Landmark size={85} className="text-white mb-4" />
                <h3 className="text-2xl font-bold">Governance</h3>
                <p className="text-sm text-white/80 mt-1">
                  Leadership & Excellence
                </p>
              </motion.div>

              {[
                { Icon: Crown, pos: "top-2 left-8" },
                { Icon: Users, pos: "top-8 right-4" },
                { Icon: Scale, pos: "bottom-4 left-8" },
                { Icon: Award, pos: "bottom-5 right-5" }
              ].map(({ Icon, pos }, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.4
                  }}
                  className={`absolute ${pos} w-14 h-14 rounded-2xl bg-white text-emerald-800 shadow-xl flex items-center justify-center`}
                >
                  <Icon size={25} />
                </motion.div>
              ))}

            </div>
          </motion.div>

        </div>
      </section>

      {/* HIGHLIGHT STRIP */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 -mt-8">

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">

          {[
            {
              icon: Landmark,
              title: "Institutional Governance",
              text: "Strategic Leadership"
            },
            {
              icon: ShieldCheck,
              title: "Academic Excellence",
              text: "Quality & Integrity"
            },
            {
              icon: Users,
              title: "Collective Leadership",
              text: "Collaborative Decisions"
            }
          ].map((item, i) => (
            <div key={i} className="p-6 flex items-center gap-4">

              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <item.icon size={24} />
              </div>

              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>

            </div>
          ))}

        </div>
      </div>

      {/* CHAIRMAN PROFILE */}
      <section id="chairman" className="py-24 px-6">

        <div className="max-w-7xl mx-auto">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              University Leadership
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Chairman of the Syndicate
            </h2>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 45 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
          >

            <div className="lg:col-span-2 relative min-h-[420px] bg-gradient-to-br from-emerald-900 to-teal-700">

              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: "radial-gradient(white 1px, transparent 1px)",
                  backgroundSize: "20px 20px"
                }}
              />

              <img
                src="/assets/img/vc-bubban.jpg"
                alt="Professor Dr. Mohammad Mohibullah Siddiquee"
                className="relative w-full h-full min-h-[420px] object-cover object-top"
              />

              <div className="absolute bottom-5 left-5 right-5 p-5 rounded-2xl bg-white/90 backdrop-blur-md">

                <p className="font-bold text-gray-900 text-lg">
                  Professor Dr. Mohammad Mohibullah Siddiquee
                </p>

                <p className="text-sm text-emerald-800 font-semibold">
                  Vice Chancellor, Bandarban University
                </p>

              </div>

            </div>

            <div className="lg:col-span-3 p-7 md:p-12">

              <span className="inline-flex items-center gap-2 text-amber-700 text-sm font-bold uppercase tracking-wider">
                <Crown size={19} />
                Syndicate Chairman
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-5 leading-tight">
                Professor Dr. Mohammad Mohibullah Siddiquee
              </h2>

              <p className="text-emerald-700 font-semibold mt-4 text-lg">
                Vice Chancellor, Bandarban University
              </p>

              <div className="h-1 w-20 bg-amber-400 rounded-full mt-7" />

              <div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-100">

                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center">
                    <Landmark size={24} />
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Leadership Position
                    </p>
                    <p className="font-bold text-gray-900">
                      Chairman of the Syndicate
                    </p>
                  </div>
                </div>

              </div>

              <p className="text-gray-600 leading-8 mt-7">
                Providing institutional leadership and strategic
                direction towards the advancement of academic
                excellence, quality education and sustainable
                university development.
              </p>

            </div>
          </motion.div>

        </div>
      </section>

      {/* SYNDICATE MEMBERS */}
      <section id="members" className="py-24 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Institutional Governance
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Members of the Syndicate
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Distinguished academic leaders, professionals and
              representatives contributing to the governance
              and strategic development of Bandarban University.
            </p>

            <div className="flex justify-center mt-7">
              <div className="flex items-center gap-3 px-6 py-3 bg-emerald-50 rounded-full text-emerald-800 font-bold">
                <Users size={20} />
                {members.length} Syndicate Members
              </div>
            </div>

          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">

            {members.map((person, i) => (
              <ProfileCard
                key={person.id}
                person={person}
                index={i}
              />
            ))}

          </div>

        </div>
      </section>

      {/* MEMBER SECRETARY */}
      <section className="py-24 px-6 bg-[#f1f7f3]">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-12">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Administrative Leadership
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Member Secretary
            </h2>

          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
          >

            <div className="lg:col-span-2 relative min-h-[390px] bg-gradient-to-br from-emerald-900 to-teal-700">

              <img
                src="/assets/img/jashim.jpeg"
                alt="Prof. Md. Jashim Uddin Khan"
                className="w-full h-full min-h-[390px] object-cover object-top"
              />

              <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-white/90 backdrop-blur-md">
                <p className="font-bold text-gray-900">
                  Prof. Md. Jashim Uddin Khan
                </p>
                <p className="text-sm text-emerald-800 font-semibold">
                  Registrar, Bandarban University
                </p>
              </div>

            </div>

            <div className="lg:col-span-3 p-8 md:p-12 flex flex-col justify-center">

              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-bold uppercase tracking-wider">
                <BriefcaseBusiness size={19} />
                Syndicate Administration
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold mt-5">
                Prof. Md. Jashim Uddin Khan
              </h2>

              <p className="text-emerald-700 font-semibold text-lg mt-3">
                Registrar, Bandarban University
              </p>

              <div className="h-1 w-20 bg-amber-400 rounded-full mt-7" />

              <p className="text-gray-600 leading-8 mt-7">
                The Member Secretary supports the administrative
                coordination of the Syndicate, facilitating official
                communication, documentation and governance-related
                activities.
              </p>

              <div className="inline-flex items-center gap-2 mt-7 px-5 py-3 rounded-xl bg-emerald-50 text-emerald-800 font-bold w-fit">
                <UserCheck size={19} />
                Member Secretary
              </div>

            </div>
          </motion.div>

        </div>
      </section>

      {/* GOVERNANCE VALUES */}
      <section className="py-20 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Principles
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Governance & Institutional Values
            </h2>

          </div>

          <div className="grid md:grid-cols-3 gap-7">

            {[
              {
                icon: Scale,
                title: "Integrity",
                description: "Promoting fairness, ethical leadership and institutional accountability."
              },
              {
                icon: BookOpen,
                title: "Academic Excellence",
                description: "Supporting quality education, research and academic development."
              },
              {
                icon: Building2,
                title: "Good Governance",
                description: "Strengthening institutional strategy, transparency and effective administration."
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                whileHover={{ y: -8 }}
                className="p-8 rounded-3xl bg-[#f8fbf9] border border-gray-100 hover:shadow-xl transition"
              >

                <div className="w-14 h-14 rounded-2xl bg-emerald-700 text-white flex items-center justify-center">
                  <item.icon size={27} />
                </div>

                <h3 className="text-xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-7 mt-3">
                  {item.description}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* VISION MISSION GOALS */}
      <section className="py-24 px-6 bg-[#f1f7f3]">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Direction
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Vision, Mission & Goals
            </h2>

          </div>

          <div className="grid md:grid-cols-3 gap-7">

            {principles.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
                className="bg-white p-8 rounded-3xl shadow-md hover:shadow-xl transition"
              >

                <div className={`w-14 h-14 ${item.color} rounded-2xl text-white flex items-center justify-center`}>
                  <item.icon size={28} />
                </div>

                <h3 className="text-2xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-8 mt-4">
                  {item.description}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="relative overflow-hidden bg-[#073b2a] text-white py-20 px-6">

        <div className="absolute w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl -right-20 -top-20" />

        <div className="relative max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <div>

              <span className="text-amber-300 font-bold uppercase tracking-[3px] text-sm">
                Get In Touch
              </span>

              <h2 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
                Leadership for a Brighter University
              </h2>

              <p className="text-gray-300 mt-5 leading-8">
                Connect with Bandarban University for institutional
                information, governance-related inquiries and
                official communication.
              </p>

              <a
                href="mailto:info@bubban.ac.bd"
                className="inline-flex items-center gap-3 mt-8 bg-amber-300 text-[#073b2a] font-bold px-7 py-4 rounded-xl hover:bg-white transition"
              >
                Contact University
                <ArrowUpRight size={20} />
              </a>

            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-7 space-y-6">

              <div className="flex gap-4 items-start">
                <MapPin className="text-amber-300 shrink-0" size={23} />
                <div>
                  <h4 className="font-bold">University Address</h4>
                  <p className="text-gray-300 text-sm mt-2 leading-7">
                    K.B Road, Shualok, Post Office: Shualok - 4600,
                    Bandarban Sadar, Bangladesh
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Phone className="text-amber-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Office Phone</h4>
                  <a href="tel:+8801318305900" className="text-gray-300 text-sm">
                    +880 1318-305900
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Mail className="text-amber-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Official Email</h4>
                  <a href="mailto:info@bubban.ac.bd" className="text-gray-300 text-sm">
                    info@bubban.ac.bd
                  </a>
                </div>
              </div>

              <div className="border-t border-white/20 pt-5">
                <p className="text-xs text-gray-400">
                  OFFICE OF THE SYNDICATE
                </p>
                <p className="font-bold mt-1">
                  Bandarban University
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
